import type {
  AutomationClassification,
  AutomationType,
} from '@playbook-technologies/common-enums';
import { httpClient } from '@client/new/api/api';

/** Stored jsonb `config`. `deeplinkUrl` is minted by creator-api from the
 *  workoutId; `includePushes` absent means true. */
export type CreatorAutomationConfig = {
  firstWorkout?: { kind: 'workout'; workoutId: number; deeplinkUrl: string };
  otpPitchTargetProductId?: string;
  includePushes?: boolean;
};

export type CreatorAutomationItem = {
  automationType: AutomationType;
  classification: AutomationClassification;
  enabled: boolean;
  updatedAt: string | null;
  config: CreatorAutomationConfig | null;
};

/** `PATCH /creator/automations/:type` body (`UpdateCreatorAutomationDto`).
 *  A config key set to null clears it from the stored config. */
export type UpdateAutomationConfigRequest = {
  firstWorkout?: { kind: 'workout'; workoutId: number } | null;
  otpPitchTargetProductId?: string | null;
  includePushes?: boolean | null;
};

export type UpdateCreatorAutomationRequest = {
  enabled?: boolean;
  config?: UpdateAutomationConfigRequest;
};

/**
 * Wire shape of `/creator/automations`. `automationType` stays a string here:
 * narrowing it to `AutomationType` is the Model's contract check, not a cast.
 * Hand-written until the endpoint is part of the creator-api Swagger document;
 * swapping it for a generated type should only touch this module.
 */
export type CreatorAutomationResponse = Omit<
  CreatorAutomationItem,
  'automationType'
> & { automationType: string };

export const getCreatorAutomations = async (
  creatorId: number,
): Promise<CreatorAutomationResponse[]> => {
  const response = await httpClient.get<CreatorAutomationResponse[]>(
    '/creator/automations',
    { params: { creatorId } },
  );
  return response.data;
};

/**
 * Wire shapes of `/creator/automations/stats` and `/creator/automations/logs`
 * (messaging-service contracts proxied by creator-api). Keys and labels are
 * server-driven; the UI renders them without a local copy map so new steps or
 * close reasons need no frontend change. Money fields are integer USD cents.
 */
export type AutomationStepInsight = {
  key: string;
  label: string;
  sent: number;
  uniqueOpens: number;
  uniqueClicks: number;
};

export type AutomationVersionBreakdown = {
  definitionVersion: number;
  isCurrent: boolean;
  active: number;
  steps: AutomationStepInsight[];
};

export type AutomationDiscrepancyReason = {
  key: string;
  label: string;
  count: number;
};

export type AutomationStatsEntry = {
  automationType: string;
  displayName: string;
  description: string;
  triggerLine: string;
  cadence: string;
  section: string;
  channels: string[];
  enrolled: number;
  converted: number;
  steps: AutomationStepInsight[];
  versions?: AutomationVersionBreakdown[];
  discrepancy: { total: number; reasons: AutomationDiscrepancyReason[] };
  influencedGmv: number;
  recoveredToDate: number;
};

export type AutomationStatsResponse = {
  stats: AutomationStatsEntry[];
  influencedGmv: number;
};

export type AutomationLogStep = {
  stepNo: number;
  sentAt: string;
  openedAt: string | null;
  clickedAt: string | null;
};

export type AutomationLogEntry = {
  id: string;
  automationType: string;
  email: string;
  subjectKey: string;
  triggerKey: string;
  definitionVersion: number;
  completedStepCount: number;
  stepCount: number;
  nextStepDueAt: string | null;
  lastStepCommittedAt: string | null;
  closedAt: string | null;
  closeReason: string | null;
  steps: AutomationLogStep[];
};

export type BrowseAutomationLogsResponse = {
  entries: AutomationLogEntry[];
  total: number;
};

export type BrowseAutomationLogsParams = {
  automationType?: AutomationType;
  email?: string;
  page: number;
  perPage: number;
};

export const getAutomationStats = async (
  creatorId: number,
  byVersion?: boolean,
): Promise<AutomationStatsResponse> => {
  const response = await httpClient.get<AutomationStatsResponse>(
    '/creator/automations/stats',
    { params: { creatorId, ...(byVersion ? { byVersion: true } : {}) } },
  );
  return response.data;
};

export const getAutomationLogs = async (
  creatorId: number,
  { automationType, email, page, perPage }: BrowseAutomationLogsParams,
): Promise<BrowseAutomationLogsResponse> => {
  const response = await httpClient.get<BrowseAutomationLogsResponse>(
    '/creator/automations/logs',
    {
      params: {
        creatorId,
        ...(automationType ? { automationType } : {}),
        ...(email ? { email } : {}),
        page,
        perPage,
      },
    },
  );
  return response.data;
};

export const updateCreatorAutomation = async (
  creatorId: number,
  automationType: AutomationType,
  body: UpdateCreatorAutomationRequest,
): Promise<CreatorAutomationResponse> => {
  const response = await httpClient.patch<CreatorAutomationResponse>(
    `/creator/automations/${automationType}`,
    body,
    { params: { creatorId } },
  );
  return response.data;
};

/**
 * Wire shape of `POST /creator/automations/send-test` (messaging-service
 * contract proxied by creator-api). `requesterEmail` is deliberately absent:
 * creator-api derives it from the session and ignores anything sent.
 */
export type SendAutomationTestRequest = {
  automationType: AutomationType;
  step: number | 'all';
  variant?: string;
  creatorId: number;
  to: string[];
  userId?: number;
};

export type SentTestMessage = {
  step: number;
  channel: string;
  templateKey: string;
  variant: string | null;
  to: string[] | number;
};

export type SkippedTestStep = {
  step: number;
  channel: string;
  reason: string;
};

export type SendAutomationTestResponse = {
  sent: SentTestMessage[];
  skipped: SkippedTestStep[];
};

export const sendAutomationTest = async (
  body: SendAutomationTestRequest,
): Promise<SendAutomationTestResponse> => {
  const response = await httpClient.post<SendAutomationTestResponse>(
    '/creator/automations/send-test',
    body,
  );
  return response.data;
};
